import { apiClient } from './client'
import type { PageResponse, WordStatus, WordSource, CefrLevel } from '@/types/common'
import type { UserWordSummary, UserWordDetail, Collection, VocabularyStats, ReviewHistory } from '@/types/user'

export interface CreateUserWordPayload {
  word: string
  translation?: string
  partOfSpeech?: string
  shortExplanation?: string
  longExplanation?: string
  personalNotes?: string
  source?: WordSource
  sourceReference?: string
  contextSentence?: string
  articleCefrLevel?: string
  senseKey?: string
}

export interface UpdateUserWordPayload {
  translation?: string
  personalNotes?: string
  status?: WordStatus
}

export interface VocabularyFilters {
  search?: string
  status?: WordStatus
  cefrLevel?: CefrLevel
  partOfSpeech?: string
  collectionId?: string
  dueOnly?: boolean
  sortBy?: 'ALPHABETICAL' | 'DATE_ADDED'
  page?: number
  size?: number
}

export interface CreateCollectionPayload {
  name: string
  description?: string
  color?: string
  icon?: string
}

export interface WordJourney {
  firstSeenAt: string
  firstSeenSource: WordSource
  firstSeenArticleId: string | null
  firstSeenArticleTitle: string | null
  encounterCount: number
  lastEncounterAt: string | null
  aiStoryEncounterCount: number
  lastAiStoryEncounterAt: string | null
  reviewStats: { total: number; correct: number; wrong: number }
  nextReviewAt: string | null
}

export interface FormEncounterStat {
  surfaceForm: string
  morphology?: string | null
  encounterCount: number
  formDescription?: string | null
  teachingTranslation?: string | null
  localizedGrammar?: string | null
  met: boolean
  firstSeenAt?: string | null
  lastSeenAt?: string | null
}

export interface FormStats {
  lemma: string
  forms: FormEncounterStat[]
}

export interface KnownForms {
  forms: string[]
}

export interface CreateUserPhrasePayload {
  phraseId?: string
  normalizedPhrase?: string
  translation?: string
  source?: WordSource
  sourceReference?: string
}

export interface UserPhraseSummary {
  id: string
  phraseId: string
  surface: string
  translation: string
  unitType: string
  status: WordStatus
  nextReviewAt?: string
  reviewCount: number
  source: WordSource
  categories: string[]
  dateAdded?: string
}

export interface KnownPhrases {
  phraseIds: string[]
  surfaces: string[]
}

export const vocabularyApi = {
  getWords: (filters: VocabularyFilters = {}) => {
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== '')
    )
    return apiClient.get<PageResponse<UserWordSummary>>('/vocabulary', { params })
  },

  getPhrases: (filters: { search?: string; status?: WordStatus; page?: number; size?: number } = {}) => {
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== '')
    )
    return apiClient.get<PageResponse<UserPhraseSummary>>('/vocabulary/phrases', { params })
  },

  addPhrase: (data: CreateUserPhrasePayload) =>
    apiClient.post<UserPhraseSummary>('/vocabulary/phrases', data),

  deletePhrase: (id: string) =>
    apiClient.delete<void>(`/vocabulary/phrases/${id}`),

  getKnownPhrases: () =>
    apiClient.get<KnownPhrases>('/vocabulary/known-phrases'),

  addWord: (data: CreateUserWordPayload) =>
    apiClient.post<UserWordDetail>('/vocabulary', data),

  getWordById: (id: string) =>
    apiClient.get<UserWordDetail>(`/vocabulary/${id}`),

  getReviewHistory: (id: string, limit = 10) =>
    apiClient.get<ReviewHistory>(`/vocabulary/${id}/review-history`, { params: { limit } }),

  setPrimarySense: (id: string, senseKey: string) =>
    apiClient.put<UserWordDetail>(`/vocabulary/${id}/primary-sense`, { senseKey }),

  /** Drops a meaning from this user's card only — shared dictionary unchanged. */
  removeSense: (id: string, senseKey: string) =>
    apiClient.delete<UserWordDetail>(`/vocabulary/${id}/senses/${encodeURIComponent(senseKey)}`),

  getJourney: (id: string) =>
    apiClient.get<WordJourney>(`/vocabulary/${id}/journey`),

  getFormStats: (id: string) =>
    apiClient.get<FormStats>(`/vocabulary/${id}/form-stats`),

  updateWord: (id: string, data: UpdateUserWordPayload) =>
    apiClient.put<UserWordDetail>(`/vocabulary/${id}`, data),

  deleteWord: (id: string) =>
    apiClient.delete<void>(`/vocabulary/${id}`),

  getStats: () =>
    apiClient.get<VocabularyStats>('/vocabulary/stats'),

  getKnownForms: () =>
    apiClient.get<KnownForms>('/vocabulary/known-forms'),

  getCollections: () =>
    apiClient.get<Collection[]>('/collections'),

  createCollection: (data: CreateCollectionPayload) =>
    apiClient.post<Collection>('/collections', data),

  updateCollection: (id: string, data: Partial<CreateCollectionPayload>) =>
    apiClient.put<Collection>(`/collections/${id}`, data),

  deleteCollection: (id: string) =>
    apiClient.delete<void>(`/collections/${id}`),

  addWordToCollection: (collectionId: string, userWordId: string) =>
    apiClient.post<void>(`/collections/${collectionId}/words/${userWordId}`),

  removeWordFromCollection: (collectionId: string, userWordId: string) =>
    apiClient.delete<void>(`/collections/${collectionId}/words/${userWordId}`),
}
